import React, { useState, useEffect, useRef, useMemo } from "react";
import { connect, type ConnectedProps } from "react-redux";
import { isEmpty } from "lodash";
import { Button } from "semantic-ui-react";
import { type AppDispatch, type RootState } from "../Application/globaltypes/redux";
import { ViewType, SortingDirection } from "../../enums";
import { OverviewHeader } from "../../components/sectionHeader";
import NoResultsWithButton from "../../components/noResultsWithButton/NoResultsWithButton";
import SearchInput from "../../components/searchInput/SearchInput";
import GenericItemsView from "../../views/ItemsView/GenericItemsView";
import { fetchDomains } from "./state/domainManagementThunk";
import { bindAction } from "../../interfaces";
import "./DomainManagement.scss";
import { getColumnOptions, columnToParamMap } from "./columnOptions";

interface DomainsOverviewProps extends PropsFromRedux {
  renderContentSwitcher?: () => React.ReactElement;
}

const DomainsOverview = ({
  domains,
  totalCount,
  isLoading,
  fetchDomains,
  renderContentSwitcher,
}: DomainsOverviewProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const reloadListViewRef = useRef<(enableSorting: boolean) => void>();

  useEffect(() => {
    reloadListViewRef.current?.(isEmpty(searchTerm));
  }, [searchTerm]);

  const handleSearchChange = (val: string) => {
    setSearchTerm(val);
    setSelectedIds([]);
  };

  const handleEdit = (id: number) => console.log(`Edit ${id}`);
  const handleDelete = (id: number) => console.log(`Delete ${id}`);

  const columnOptions = useMemo(() => getColumnOptions({ handleEdit, handleDelete }), []);

  const fetchData = (
    skip: number,
    top: number,
    sortColumnName: string = "",
    sortDirection: SortingDirection = SortingDirection.Ascending,
  ) => {
    let mappedSortKey;

    if (sortColumnName) {
      // @ts-ignore
      mappedSortKey = columnToParamMap[sortColumnName.toLowerCase()];
    }

    fetchDomains({
      top,
      skip,
      sortBy: mappedSortKey,
      sortOrder: sortDirection,
      term: searchTerm,
    });
  };

  const noResultsContent = (
    <NoResultsWithButton
      title="You don't have any Domains"
      description="Looks like you haven't created any domains yet."
      actionText="Add a new domain using the button bellow."
      iconClassName="fa-box-open"
      actionButton={<Button primary>Add Domain</Button>}
      filtered={!!searchTerm}
    />
  );

  return (
    <section className="nested-content domain-management">
      <OverviewHeader title="Domain Management" itemName="Domain" selectedItemsAmount={selectedIds.length}>
        <Button primary>Add Domain</Button>
      </OverviewHeader>

      <GenericItemsView
        items={domains}
        dataCount={totalCount}
        isLoading={isLoading}
        columnOptions={columnOptions}
        fetchData={fetchData}
        viewType={ViewType.LIST}
        disableListViewButton
        selectedIds={selectedIds}
        onSelectedItemChanged={setSelectedIds}
        noResultsContent={noResultsContent}
        setReloadListItems={(reloadListItems) => (reloadListViewRef.current = reloadListItems)}
        customHeaderContent={renderContentSwitcher?.()}
        renderSearch={() => (
          <SearchInput placeholder="Search for Domains..." onChange={handleSearchChange} defaultValue={searchTerm} />
        )}
      />
    </section>
  );
};

const mapStateToProps = (state: RootState) => ({
  domains: state.domainManagement.items,
  totalCount: state.domainManagement.itemsCount,
  isLoading: state.domainManagement.isLoading,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  fetchDomains: bindAction(fetchDomains, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedDomainsOverview = connector(DomainsOverview);
export default ConnectedDomainsOverview;
